import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Form, Input, Button, Select, AutoComplete, Radio, Checkbox, Tooltip, Space } from "antd";
import { Country, State } from "country-state-city";
import { editTaxManagement } from "redux/actions";
import { TYPE_OF_THE_TAX } from "shared/constants/AppEnums";
import { InfoCircleOutlined } from "@ant-design/icons";

const formItemLayout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
};

const CountryList = Country.getAllCountries().map((data) => {
  return {
    name: data.name,
    countryCode: data.isoCode,
  };
});

const StateList = (countryCode) =>
  State.getStatesOfCountry(countryCode).map((data) => {
    return {
      name: data.name,
      stateCode: data.isoCode,
    };
  });

const AllState = State.getAllStates();

function EditTaxManagement({ data, closeModel }) {
  const formRef = useRef();

  const dispatch = useDispatch();
  const [countries, setCountries] = useState(CountryList);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState([]);
  const [applyToAllStates, setApplyToAllStates] = useState(false);
  const [form] = Form.useForm();

  const [formState, setFormState] = useState({
    taxName: data?.taxName,
    taxName_es: data?.taxName_es,
    countryCode: data?.countryCode,
    postalCode: data?.postalCode,
    stateCodes: Array.isArray(data?.stateCodes)
      ? data.stateCodes.map(
          code => AllState.find(item => item.isoCode === code && item.countryCode === data?.countryCode)?.name ?? code
        )
      : [],
    tarifPercentage: data?.tarifPercentage,
    shipping: data?.shipping,
    kindOfTax: data?.kindOfTax,
  });

  useEffect(() => {
    setStates(StateList(formState?.countryCode) ?? []);
    
    // Check if the stateCodes array contains a wildcard "*" (applies to all states)
    if (Array.isArray(data?.stateCodes) && data.stateCodes.includes("*")) {
      setApplyToAllStates(true);
      setSelectedState([]);
    } else {
      setApplyToAllStates(false);
      setSelectedState(formState?.stateCodes || []);
    }
  }, [formState?.countryCode, data?.stateCodes]);

  const onSubmit = (values) => {
    let selectedStateCodes;
    
    // If apply to all states is checked, use wildcard "*" to indicate all states
    if (applyToAllStates) {
      selectedStateCodes = ["*"];
    } else {
      // Map selected state names to state codes
      selectedStateCodes = selectedState.map((stateName) => {
        const stateObj = states.find(item => item.name === stateName);
        return stateObj ? stateObj.stateCode : null;
      }).filter(Boolean);
    }
    
    const payload = {
      ...values,
      id: data._id,
      countryCode: formState?.countryCode,
      stateCodes: selectedStateCodes,
    };
    dispatch(editTaxManagement(payload));
    closeModel();
  };

  return (
    <Form
      className="edit-customer-form"
      name="validate_other"
      layout="vertical"
      {...formItemLayout}
      onFinish={onSubmit}
      ref={formRef}
      initialValues={formState}
      form={form}
    >
      <Form.Item
        name="taxName"
        label="Tax Name"
        rules={[
          { required: true, whitespace: true, message: "Tax Name is required" },
        ]}
      >
        <Input placeholder="Enter Tax Name" />
      </Form.Item>
      <Form.Item
        name="taxName_es"
        label="Nombre del impuesto"
        rules={[
          {
            required: true,
            whitespace: true,
            message: "El nombre fiscal es obligatorio",
          },
        ]}
      >
        <Input placeholder="Ingrese el nombre del impuesto en español" />
      </Form.Item>
      <Form.Item
        name="countryCode"
        label="Country Code"
        rules={[
          {
            required: "true",
            whitespace: true,
            message: "Country Code is required",
          },
        ]}
      >
        <Select
          allowClear
          onChange={(val) => {
            setFormState((prev) => ({
              ...prev,
              countryCode: val,
            }));
            formRef.current?.setFieldsValue({ stateCodes: [] });
          }}
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          options={countries?.map((item) => ({
            value: item.countryCode,
            label: item.name,
          }))}
        />
      </Form.Item>
      <Form.Item 
        label={
          <Space>
            State/Province
            <Tooltip title="Select specific states or apply tax to the entire country">
              <InfoCircleOutlined />
            </Tooltip>
          </Space>
        } 
        name="stateCodes"
      >
        <div style={{ marginBottom: "10px" }}>
          <Checkbox
            checked={applyToAllStates}
            onChange={(e) => {
              setApplyToAllStates(e.target.checked);
              if (e.target.checked) {
                // If applying to all states, disable state selection
                setSelectedState([]);
                formRef.current?.setFieldsValue({ stateCodes: [] });
              }
            }}
          >
            Apply to entire country (all states/provinces)
          </Checkbox>
        </div>
        
        {!applyToAllStates && (
          <Select
            mode="multiple"
            showSearch
            style={{ width: '100%' }}
            placeholder="Select specific states/provinces"
            value={selectedState}
            disabled={applyToAllStates}
            onChange={(val) => {
              setSelectedState(val);
              formRef.current?.setFieldsValue({ stateCodes: val });
            }}
            options={states?.map((item) => ({
              value: item.name,
              label: item.name,
            }))}
            filterOption={(inputValue, option) =>
              (option?.label ?? "")
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
          />
        )}
      </Form.Item>
      <Form.Item
        name="tarifPercentage"
        label="Tax Percentage"
        rules={[
          {
            required: true,
            message: "Please enter tax percentage",
          },
        ]}
      >
        <Input placeholder="Enter Tax Percentage" />
      </Form.Item>

      <Form.Item name="shipping" label="isShipping">
        <Radio.Group name="shipping">
          <Radio value={true}>Yes</Radio>
          <Radio value={false}>No</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        label="Kind OF Tax"
        name="kindOfTax"
        rules={[{ required: true, message: "Please Select Tax Type" }]}
      >
        <Select
          allowClear
          optionFilterProp="children"
          placeholder="Select Tax Type"
        >
          {TYPE_OF_THE_TAX?.map((ct) => {
            return <Select.Option key={ct.value}>{ct.name}</Select.Option>;
          })}
        </Select>
      </Form.Item>
      <Form.Item
        wrapperCol={{
          span: 12,
          offset: 10,
        }}
      >
        <Button type="primary" htmlType="submit">
          Save
        </Button>
      </Form.Item>
    </Form>
  );
}

export default EditTaxManagement;
EditTaxManagement.propTypes = {
  data: PropTypes.object,
  closeModel: PropTypes.func,
};
